import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Homologacion } from '../types';
import { defaultHomologacion as localDefaultHomologacion } from '../config/defaultHomologacion';
import { captureException } from '../utils/captureException';

interface UseDefaultHomologacionResult {
  defaultHomologacion: Homologacion | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Global cache to avoid refetching on every component mount
let cachedHomologacion: Homologacion | null = null;
let fetchPromise: Promise<Homologacion> | null = null;
let isFetching = false;

export const useDefaultHomologacion = (): UseDefaultHomologacionResult => {
  const { user } = useAuth();
  // Initialize with cached data or local fallback
  const [defaultHomologacion, setDefaultHomologacion] = useState<Homologacion | null>(
    cachedHomologacion || localDefaultHomologacion
  );
  const [loading, setLoading] = useState<boolean>(!cachedHomologacion && !!user);
  const [error, setError] = useState<string | null>(null);

  const fetchDefaultHomologacion = useCallback(async () => {
    // If we have cached data, use it immediately
    if (cachedHomologacion) {
      setDefaultHomologacion(cachedHomologacion);
      setLoading(false);
      return;
    }

    // If user is not authenticated, use local fallback
    if (!user) {
      setDefaultHomologacion(localDefaultHomologacion);
      setLoading(false);
      return;
    }

    // If already fetching, wait for the existing promise
    if (isFetching && fetchPromise) {
      try {
        const data = await fetchPromise;
        setDefaultHomologacion(data);
        setLoading(false);
        return;
      } catch (err) {
        captureException(err, { tags: { feature: 'useDefaultHomologacion' }, extra: { action: 'fetchDefaultHomologacion' } });
        // If fetch fails, fallback to local
        setDefaultHomologacion(localDefaultHomologacion);
        setError(err instanceof Error ? err.message : 'Failed to fetch homologacion');
        setLoading(false);
        return;
      }
    }

    // Start new fetch
    isFetching = true;
    setLoading(true);
    setError(null);

    fetchPromise = (async (): Promise<Homologacion> => {
      try {
        const idToken = await user.getIdToken();
        if (!idToken) {
          throw new Error('No idToken found');
        }

        const url = `${process.env.REACT_APP_API_URL || ''}/api/homologacion/default`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Cache the result only from backend response (never update after initial fetch)
        if (!cachedHomologacion) {
          cachedHomologacion = data;
        }
        setDefaultHomologacion(data);
        setLoading(false);
        isFetching = false;
        fetchPromise = null;
        
        return data;
      } catch (err) {
        isFetching = false;
        fetchPromise = null;
        
        // Fallback to local config on error (do not cache local fallback)
        console.warn('Failed to fetch default homologacion from backend, using local fallback:', err);
        setDefaultHomologacion(localDefaultHomologacion);
        setError(err instanceof Error ? err.message : 'Failed to fetch homologacion');
        setLoading(false);
        
        return localDefaultHomologacion;
      }
    })();

    try {
      await fetchPromise;
    } catch (err) {
      captureException(err, { tags: { feature: 'useDefaultHomologacion' }, extra: { action: 'fetchDefaultHomologacion' } });
      // Error already handled in the promise
    }
  }, [user]);

  useEffect(() => {
    fetchDefaultHomologacion();
  }, [fetchDefaultHomologacion]);

  const refetch = useCallback(() => {
    // Clear cache and fetch again
    cachedHomologacion = null;
    fetchPromise = null;
    isFetching = false;
    fetchDefaultHomologacion();
  }, [fetchDefaultHomologacion]);

  return {
    defaultHomologacion,
    loading,
    error,
    refetch,
  };
};

